import { z } from 'zod';
import { AxonEventOriginValueSchema, AxonEventTypeSchema, type AxonEvent } from './event.types.js';

export const AxonViewSchema = z.object({
  id: z.string(),
  created_at_ms: z.number(),
  name: z.string().nullish(),
  last_event_at_ms: z.number().optional(),
});
export type AxonView = z.infer<typeof AxonViewSchema>;

export const AxonEventOriginSchema = AxonEventOriginValueSchema;
export type AxonEventOrigin = z.infer<typeof AxonEventOriginSchema>;

export const PublishEventOriginSchema = z.enum(['EXTERNAL_EVENT', 'AGENT_EVENT', 'USER_EVENT']);
export type PublishEventOrigin = z.infer<typeof PublishEventOriginSchema>;

export const AxonEventViewSchema = z.object({
  sequence: z.number(),
  axon_id: z.string(),
  timestamp_ms: z.number(),
  origin: AxonEventOriginSchema,
  source: z.string(),
  event_type: AxonEventTypeSchema,
  payload: z.string(),
});
export type AxonEventView = z.infer<typeof AxonEventViewSchema>;

/**
 * Parse a JSON event payload, with a fallback for the Axon bridge bug that
 * sometimes appends an extra trailing `}`. Returns `null` when the value is
 * not parseable JSON so callers can fall back to the raw string.
 */
export function safeParseJsonPayload(raw: unknown): unknown {
  if (raw && typeof raw === 'object') return raw;
  if (typeof raw !== 'string') return null;
  try {
    return JSON.parse(raw);
  } catch {
    try {
      return JSON.parse(raw.replace(/\}\s*\}$/, '}'));
    } catch {
      return null;
    }
  }
}

/**
 * The single conversion from the broker wire shape to the in-process
 * {@link AxonEvent}. Every ingress that receives an {@link AxonEventView} —
 * the server's own Axon reads, and the SDK transports that agent providers
 * bridge through `injectAxonEventView` — goes through here.
 *
 * The `Required<AxonEvent>` return type is the guardrail, not decoration.
 * `AxonEvent.sequence` is optional, because locally-synthesized events (the
 * Axon-unavailable publish fallback) have no upstream sequence, while
 * `AxonEventView.sequence` is required — so an inline literal that drops it
 * type-checks, and everything keyed off the sequence silently no-ops: the fold
 * CAS, loser silence, and the `applied_seq` cursor advance. `Required` makes
 * any unmapped `AxonEvent` field a compile error here.
 */
export function axonEventFromView(view: AxonEventView): Required<AxonEvent> {
  return {
    id: `${view.axon_id}:${view.sequence}`,
    sequence: view.sequence,
    streamId: view.axon_id,
    type: view.event_type,
    payload: safeParseJsonPayload(view.payload) ?? view.payload,
    timestamp: view.timestamp_ms,
    origin: view.origin,
    source: view.source,
  };
}

export const PublishParamsSchema = z.object({
  source: z.string(),
  event_type: AxonEventTypeSchema,
  origin: PublishEventOriginSchema,
  payload: z.string(),
});
export type PublishParams = z.infer<typeof PublishParamsSchema>;

export const PublishResultViewSchema = z.object({
  sequence: z.number(),
  timestamp_ms: z.number(),
});
export type PublishResultView = z.infer<typeof PublishResultViewSchema>;

export const AxonListViewSchema = z.object({
  axons: z.array(AxonViewSchema),
  has_more: z.boolean().optional(),
  total_count: z.number().optional(),
});
export type AxonListView = z.infer<typeof AxonListViewSchema>;

export const AxonListParamsSchema = z.object({
  limit: z.number().int().positive().optional(),
  starting_after: z.string().optional(),
});
export type AxonListParams = z.infer<typeof AxonListParamsSchema>;
